import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { authAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import { Capacitor } from '@capacitor/core';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const verifyCalled = React.useRef(false);
  
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('Verifying your email...');
  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);

  const handleResend = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setResending(true);
    try {
      await authAPI.resendVerification(email);
      toast.success('Verification email sent! Please check your inbox.');
      setEmail(''); // Clear input
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to send verification email');
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link.');
      return;
    }

    if (verifyCalled.current) return;
    verifyCalled.current = true;

    const verifyEmail = async () => {
      try {
        await authAPI.verifyEmail(token);
        setStatus('success');
        setMessage('Email verified successfully! You can now sign in.');
        // toast.success('Email verified successfully!');
      } catch (error: any) {
        setStatus('error');
        setMessage(error.response?.data?.error || 'Failed to verify email. The link may be invalid or expired.');
        toast.error('Verification failed');
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <Link to={Capacitor.isNativePlatform() ? "/login" : "/home"}>
            <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center shadow-lg cursor-pointer hover:opacity-90 transition-opacity">
              <span className="text-2xl font-bold text-white">C</span>
            </div>
          </Link>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
          Email Verification
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
          {status === 'verifying' && (
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black dark:border-white mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">{message}</p>
            </div>
          )}

          {status === 'success' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center"
            >
              <CheckCircleIcon className="h-16 w-16 text-green-500 mb-4" />
              <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">Verified!</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
              <Link
                to="/login"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black dark:focus:ring-white"
              >
                Sign in
              </Link>
            </motion.div>
          )}

          {status === 'error' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center"
            >
              <XCircleIcon className="h-16 w-16 text-red-500 mb-4" />
              <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">Verification Failed</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
              
              <div className="w-full mb-6">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Need a new verification link?</p>
                <div className="flex flex-col space-y-3">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-black dark:focus:ring-white focus:border-black dark:focus:border-white sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resending}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black dark:focus:ring-white disabled:opacity-50"
                  >
                    {resending ? 'Sending...' : 'Resend Link'}
                  </button>
                </div>
              </div>

              <Link
                to="/login"
                className="text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300 font-medium underline"
              >
                Back to Sign in
              </Link>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
